import { formatDate } from '@angular/common';
import { Injectable } from '@angular/core';
import { IApplicationFormIndex, IApplicationFormQuestionSet, IHttpResponse, IMessage, IQuoteableCarriersProduct } from '@boldpenguin/sdk';
import {
  BqxApplicationAbstractService,
  IApplicationCloneParam,
  IApplicationFormFactory,
  IApplicationSearchFactory,
  IApplicationSearchParam,
  ISearchableAnswer,
  ISearchableQuestionParam
} from 'bqx-application';
import { AbstractFlagrService, Constraints, FlagKey } from 'bqx-feature-switches';
import { LogLevel, LoggerAbstractService } from 'bqx-logging';
import { NGXLogger } from 'ngx-logger';
import { BehaviorSubject, Observable, ReplaySubject, Subject, combineLatest, of } from 'rxjs';
import { catchError, finalize, map, takeUntil, tap } from 'rxjs/operators';
import { AppConfig } from 'src/app/app.config';
import { ApplicationFormState, ApplicationQuestionSetCodes, SDKSessionItem } from 'src/app/enums/sdk-application.enum';
import { IAnswer, IApplication, IChoice, IQuestion, IQuestionSet } from 'src/app/models/application-form/application-form.model';
import { Global } from 'src/app/models/shared/app-global.model';
import { FormDataAbstractService } from '../form-data/form-data.abstract.service';
import { InputFocusAbstractService } from '../input-focus/input-focus.abstract.service';
import { ApplicationFormAbstractService } from './application-form.abstract.service';

@Injectable({
  providedIn: 'root'
})
export class ApplicationFormService implements ApplicationFormAbstractService {
  private static LOG_NAME = 'Application Form Event';
  private static CREATE_APPLICATION_EVENT = 'CreateNewApplication';
  private static QUESET_SAVE_EVENT = 'saveQuestionSet';
  private static ACTIVE_QUESET_UPDATE_EVENT = 'changeActiveQuestionSet';
  private static BUSINESS_INFO_STATE_EVENT = 'updateFormState';
  private _application$: BehaviorSubject<IApplication> = new BehaviorSubject<IApplication>(null);
  private _isAppFormRedirected$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  private _isAppSyncedWithBP$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  private _activeQuestionComplete$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  private _activeQuestionSet$: BehaviorSubject<IQuestionSet> = new BehaviorSubject<IQuestionSet>(null);
  private _activeQuestionSetId$: BehaviorSubject<string> = new BehaviorSubject<string>(null);
  private _nonEmptyQuestionSets$: BehaviorSubject<IQuestionSet[]> = new BehaviorSubject<IQuestionSet[]>(null);
  private _applicationQuoteRequests$: BehaviorSubject<any[]> = new BehaviorSubject<any[]>(null);
  private _appFormState$: BehaviorSubject<string> = new BehaviorSubject<string>(null);
  private _appForm: IApplication;
  private _objBpApplication: IApplicationFormFactory;
  private _objBpSearch: IApplicationSearchFactory<
    IHttpResponse<[IApplicationFormIndex]>,
    IApplicationSearchParam,
    IApplicationCloneParam,
    ISearchableQuestionParam,
    ISearchableAnswer[]
  >;
  private _objFormUnSub$ = new Subject<void>();
  private _objQueSetUnSub$ = new Subject<void>();
  private _objQueSetsUnSub$ = new Subject<void>();
  private _objNEQueSetsUnSub$ = new Subject<void>();
  private _objSdkStateUnSub$ = new Subject<void>();
  private _objQueSetLoadingUnSub$ = new Subject<void>();
  private _objSDKAppFormLoadingUnSub$ = new Subject<void>();
  private _objQuotesLoadingUnSub$ = new Subject<void>();
  private _objQuotesReqUnSub$ = new Subject<void>();
  private _objOwnerUnSub$ = new Subject<void>();
  private _objLockedUnSub$ = new Subject<void>();
  private _objTakenOverUnSub$ = new Subject<void>();
  private _objClonedUnSub$ = new Subject<void>();
  private _objSkipCurrentTierUnSub$ = new Subject<void>();
  private _objInactiveUnSub$ = new Subject<void>();
  private _objFormLoadedUnSub$ = new Subject<void>();
  private _objGetMessagesUnSub$ = new Subject<void>();
  private _objCarrierProductsUnSub$ = new Subject<void>();
  private _isActiveQuestionSetLoading$ = new BehaviorSubject<boolean>(false);
  private _isSDKFormLoading$ = new BehaviorSubject<boolean>(false);
  private _isBridgingToSDKQSet$ = new ReplaySubject<boolean>();
  private _areQuotesLoading$ = new BehaviorSubject<boolean>(false);
  private _readOnlyMode$ = new BehaviorSubject<boolean>(false);
  private _isHandshakeDealCall = false;
  private _isOwner$ = new ReplaySubject<boolean>();
  private _isLocked$ = new ReplaySubject<boolean>();
  private _isTakenOver$ = new ReplaySubject<boolean>();
  private _isFormCloned$ = new ReplaySubject<boolean>();
  private _isFormInactive$ = new ReplaySubject<boolean>();
  private _isFormLoaded$ = new ReplaySubject<boolean>();
  private _selectLegacyMessages$ = new BehaviorSubject<IMessage[]>([]);
  private _showConsentAcknowledgmentCheckbox$ = new ReplaySubject<boolean>();
  private _isOwner: boolean;
  private _carrierProducts$ = new ReplaySubject<IQuoteableCarriersProduct[]>();
  private _isEOMLoading$ = new BehaviorSubject<boolean>(false);
  private _isReferred = false;
  private _sdkActiveQueSetTriggered = false;
  private _today = new Date();
  private _timeZone = 'US/Eastern';
  private _flagr_date = Number(formatDate(this._today, 'yyyyMMdd', 'en-US', this._timeZone));
  private _flagr_time = Number(formatDate(this._today, 'HHmm', 'en-US', this._timeZone));
  private _flagr_day = this._today.toLocaleString('en-US', { timeZone: this._timeZone, weekday: 'long' });
  private readonly _VIRTUAL_PAGE_NAME: string = '/application_form';
  private readonly _BUSINESS_INFO_TITLE: string = 'Business Info';
  private readonly _CONSENT_TITLE: string = 'Consent';
  private readonly _CONSENT_CODE: string = 'send_to_bold_penguin';
  private readonly _MQS_PARENT_TITLE: string = 'MQS';
  private readonly _MQS_NOPREFIX_CHILD_TITLES = new Set<string>(['General information', 'Business type', 'Coverage type', 'Consent']);

  public get activeQuestionComplete$(): BehaviorSubject<boolean> {
    return this._activeQuestionComplete$;
  }

  get isBridgingToSDKQSet$(): Observable<boolean> {
    return this._isBridgingToSDKQSet$.asObservable();
  }

  public get areQuotesLoading$(): BehaviorSubject<boolean> {
    return this._areQuotesLoading$;
  }

  public get appFormState$(): BehaviorSubject<string> {
    return this._appFormState$;
  }

  public get isAppFormRedirected$(): BehaviorSubject<boolean> {
    return this._isAppFormRedirected$;
  }

  public get isAppSyncedWithBP$(): BehaviorSubject<boolean> {
    return this._isAppSyncedWithBP$;
  }

  public get application$(): BehaviorSubject<IApplication> {
    return this._application$;
  }

  public get activeQuestionSet$(): BehaviorSubject<IQuestionSet> {
    return this._activeQuestionSet$;
  }

  public get nonEmptyQuestionSets$(): BehaviorSubject<IQuestionSet[]> {
    return this._nonEmptyQuestionSets$;
  }

  public get activeQuestionSetId$(): BehaviorSubject<string> {
    return this._activeQuestionSetId$;
  }
  public get quoteRequests$(): BehaviorSubject<any[]> {
    return this._applicationQuoteRequests$;
  }

  public get isActiveQuestionSetLoading$(): BehaviorSubject<boolean> {
    return this._isActiveQuestionSetLoading$;
  }

  public get isSDKAppFormLoading$(): BehaviorSubject<boolean> {
    return this._isSDKFormLoading$;
  }

  public get readOnlyMode$(): BehaviorSubject<boolean> {
    return this._readOnlyMode$;
  }

  public get showConsentAcknowledgmentCheckbox$(): Observable<boolean> {
    return this._showConsentAcknowledgmentCheckbox$.asObservable();
  }

  //This property is to use toggle between Take-Over Application or View/Edit Application.
  public get isOwner$(): Observable<boolean> {
    return this._isOwner$.asObservable();
  }

  public get isLocked$(): Observable<boolean> {
    return this._isLocked$.asObservable();
  }

  public get isTakenOver$(): Observable<boolean> {
    return this._isTakenOver$.asObservable();
  }

  public get isFormCloned$(): Observable<boolean> {
    return this._isFormCloned$.asObservable();
  }

  get isFormInactive$(): Observable<boolean> {
    return this._isFormInactive$.asObservable();
  }

  public get isFormLoaded$(): Observable<boolean> {
    return this._isFormLoaded$.asObservable();
  }

  public get selectLegacyMessages$(): Observable<IMessage[]> {
    return this._selectLegacyMessages$.asObservable();
  }

  public get carrierProducts$(): Observable<IQuoteableCarriersProduct[]> {
    return this._carrierProducts$.asObservable();
  }
  get isEOMLoading$(): Observable<boolean> {
    return this._isEOMLoading$.asObservable();
  }

  public get isReferred(): boolean {
    return this._isReferred;
  }

  public isInTransition = false;
  public get isTakingOver(): boolean {
    if (this._isOwner) this.isInTransition = false;
    return this.isInTransition;
  }
  public dashboardFlag = false;
  public searchPageUser = null;
  public searchPageGUID = null;

  public get isHandshakeDealCall(): boolean {
    return this._isHandshakeDealCall;
  }
  public set isHandshakeDealCall(value: boolean) {
    this._isHandshakeDealCall = value;
  }

  constructor(
    private bqxApplication: BqxApplicationAbstractService,
    private focus: InputFocusAbstractService,
    private loggerService: LoggerAbstractService,
    private logger: NGXLogger,
    private formDataService: FormDataAbstractService,
    private flagrService: AbstractFlagrService
  ) {
    this.isOwner$.subscribe((val) => {
      this._isOwner = val;
    });
  }

  public createNewApplication(): void {
    this.subscribeToApplication();
    const formid = this.formDataService.FormId;
    if (sessionStorage.getItem(SDKSessionItem.UID_TOKEN) === null || sessionStorage.getItem(SDKSessionItem.UID_TOKEN) === undefined) {
      if (Global.applicationId != formid) {
        this.loggerService.postLogData(LogLevel.Info, 'New Application: applicationId', JSON.stringify({ initialApplicationId: formid }));
      }
      sessionStorage.setItem('applicationId', formid);
      Global.applicationId = formid;
      const application = {
        id: Global.sessionId,
        form: {
          id: formid,
          state: 'in-progress'
        },
        questionSets: {
          active: '',
          activeAnswerId: '',
          list: this.formDataService.QuestionSets
        },
        quotes: null
      };

      this._activeQuestionSet$.next(this.formDataService.ContactQuestionSet);
      this._application$.next(application);
      this.changeActiveQuestionSet(1);
      const event = {
        type: ApplicationFormService.CREATE_APPLICATION_EVENT,
        data: application
      };
      console.log(ApplicationFormService.LOG_NAME, event);
    } else {
      const application = {
        id: Global.sessionId,
        form: {
          id: formid,
          state: 'in-progress'
        },
        questionSets: {
          active: '',
          activeAnswerId: '',
          list: []
        },
        quotes: null
      };
      this._application$.next(application);
      this.loadSdkApplication(false);
    }
    if (!this.flagrService.hasFlagrBeenCalled) {
      const flagrKeys: FlagKey[] = AppConfig.settings.flagr.flagKeys;
      const constraints: Constraints = { day: this._flagr_day, date: this._flagr_date, time: this._flagr_time };
      const quoteNumber = sessionStorage.getItem('quoteNumber')?.replaceAll('-', '');
      const ssid = sessionStorage.getItem('ssid') ? sessionStorage.getItem('ssid') : formid;

      this.flagrService
        .callFlagr(quoteNumber, flagrKeys, ssid, constraints)
        .pipe(
          tap(() => {
            this.flagrService.setFlagrCalled();
          }),
          catchError((error) => {
            this.flagrService.setFlagrCalled();
            this.loggerService.postLogData(LogLevel.Error, 'Flagr API call failed', error, '');
            return of(error);
          }),
          tap((response) => {
            if (response instanceof Error) {
              this.logger.error(response);
            }
          }),
          finalize(() => {
            sessionStorage.removeItem('quoteNumber');
            sessionStorage.removeItem('ssid');
          })
        )
        .subscribe();
    }
  }

  public loadSdkApplication(isPrefill = true): void {
    const option = {
      applicationType: 'Agent',
      prefillData: isPrefill ? this.createPrefillData() : null
    };
    this._objBpApplication = this.bqxApplication.getApplicationForm();
    this._objBpApplication.initApplication(option);
    this.initFormDataListener();
  }

  public resetSdkApplicationForm(): void {
    if (this._objBpApplication) {
      this._objBpApplication.resetApplicationForm();
      this.resetLocal();
      this.formDataService.ResetData();
    }
  }

  public bindQuote(appFormId: string, quoteId: string): Observable<string> {
    return this._objBpApplication.bindQuote(appFormId, quoteId);
  }

  public exploreOtherMarkets(): void {
    this._isEOMLoading$.next(true);
    const appForm = this._application$.getValue();
    if (appForm && this._objBpApplication)
      this._objBpApplication
        .skipCurrentTier(appForm.form.id)
        .pipe(takeUntil(this._objSkipCurrentTierUnSub$))
        .subscribe(
          (response) => {
            this._isEOMLoading$.next(false);
            this._objSkipCurrentTierUnSub$.next();
            this._objSkipCurrentTierUnSub$.complete();
          },
          (err) => {
            //This is bug from BP that it is always coming to empty error but at this point the actual skipCurrentTier method in BP is a promise
            // and we can call and forgot, but we are using observable for _isEOMLoading$ (spinner) behavior.
            this._isEOMLoading$.next(false);
            this._objSkipCurrentTierUnSub$.next();
            this._objSkipCurrentTierUnSub$.complete();
            this.loggerService.postLogData(LogLevel.Error, 'application-form-service: exploreOtherMarkets skipCurrentTier', err, '');
          }
        );
  }

  //This function is to take over the application from different owner.
  public cloneApplicationForm(params: IApplicationCloneParam): void {
    const option = {
      applicationType: 'Agent'
    };
    this._objBpSearch = this.bqxApplication.getSearchForm(option);
    this._objBpSearch.cloneApplicationForm(params);
  }
  private resetLocal() {
    this.destroyOpenSubscription();
    this._application$ = new BehaviorSubject<IApplication>(null);
    this._isAppFormRedirected$ = new BehaviorSubject<boolean>(false);
    this._isAppSyncedWithBP$ = new BehaviorSubject<boolean>(false);
    this._activeQuestionComplete$ = new BehaviorSubject<boolean>(false);
    this._activeQuestionSet$ = new BehaviorSubject<IQuestionSet>(null);
    this._applicationQuoteRequests$ = new BehaviorSubject<any[]>(null);
    this._appFormState$ = new BehaviorSubject<string>(null);
    this._appForm = null;
    //Temporary solution - starts
    //TODO: need to dispose the this._objBpApplication (BQX Core changes required)
    this._objBpApplication.applicationQuestionSet$.next(null);
    this._objBpApplication.applicationQuestionSets$.next([]);
    this._objBpApplication.applicationNonEmptyQuestionSets$.next([]);
    this._objBpApplication.applicationParentQuestionSets$.next([]);
    this._objBpApplication.applicationQuoteRequests$.next([]);
    this._objBpApplication.applicationForm$.next(null);
    //Temporary solution - ends
    this._isActiveQuestionSetLoading$ = new BehaviorSubject<boolean>(false);
    this._areQuotesLoading$ = new BehaviorSubject<boolean>(false);
  }

  public getQuestionSet(code: string): IQuestionSet {
    let questionSet: IQuestionSet = undefined;
    const app = this._appForm;
    if (app) {
      const filterVal = app.questionSets.list.filter((item) => item.question_set.code === code && item.is_blank === false);
      if (filterVal && filterVal.length > 0) {
        questionSet = filterVal[0];
      }
    }
    return questionSet;
  }

  public getQuestionByCode(code: string): IQuestion {
    const app = this._appForm;
    let question: IQuestion;
    if (app) {
      for (let index = 0; index < app.questionSets.list.length; index++) {
        const queSet = app.questionSets.list[index];
        const ques = queSet.answers.filter((ans) => ans.question.code === code);
        if (ques && ques.length > 0) {
          question = ques[0].question;
          break;
        }
      }
    }
    return question;
  }

  public getAnswerByCode(code: string): IAnswer {
    const app = this._appForm;
    let answer: IAnswer;
    if (app) {
      for (let index = 0; index < app.questionSets.list.length; index++) {
        const queSet = app.questionSets.list[index];
        const ans = queSet.answers.filter((ans) => ans.question.code === code);
        if (ans && ans.length > 0) {
          answer = ans[0];
          break;
        }
      }
    }
    return answer;
  }

  public changeActiveQuestionSet(routeOrder: number, routId?: string): void {
    if (routeOrder === 0) {
      this.isAppFormRedirected$.next(true);
      return;
    }
    const activeQueSet = routId
      ? this._appForm.questionSets.list.find((set) => set.order === routeOrder && set.id === routId && set.is_blank === false)
      : this._appForm.questionSets.list.find((set) => set.order === routeOrder && set.is_blank === false);
    if (!this._isAppSyncedWithBP$.value) {
      this._appForm.questionSets.active = activeQueSet.question_set_id;
      this._application$.next(this._appForm);
      this._activeQuestionSet$.next(activeQueSet);

      if (activeQueSet.order >= 4) {
        this.isAppFormRedirected$.next(true);
      } else {
        this.isAppFormRedirected$.next(false);
      }

      const event = {
        type: ApplicationFormService.ACTIVE_QUESET_UPDATE_EVENT,
        data: this._appForm
      };
      console.log(ApplicationFormService.LOG_NAME, event);
    } else {
      this._objBpApplication.updateActiveQuestionSet(activeQueSet.question_set.code);
      if (activeQueSet.order >= 4) {
        this.isAppFormRedirected$.next(true);
      } else {
        this.isAppFormRedirected$.next(false);
        this.updateBusinessInfoState();
      }
      this._activeQuestionSet$.next(activeQueSet);
    }
    this.subscribeControlToFocus();
  }

  public subscribeControlToFocus(): void {
    this.focus.resetTotalInputsBuilt();
    this.focus.inputsOnDom$.isStopped = false;
    this.focus.inputsOnDom$.subscribe(
      (loadedInputs: number) => {
        if (this._activeQuestionSet$.value && this._activeQuestionSet$.value.question_set.code !== 'progressive_commercial_contact') {
          if (
            loadedInputs ===
            this._activeQuestionSet$.value.answers.filter(
              (ans) =>
                ans.question.field_type !== 'label' &&
                ans.question.field_type !== 'anchor' &&
                ans.question.code !== 'evanston_activity_inventory_percentage_total' &&
                ans.question.code !== 'progressive_commercial_jornaya_lead_id' &&
                ans.question.code !== 'mqs_business_personal_property_limit' &&
                !ans.question.is_hidden &&
                ans.active
            ).length
          ) {
            const questionCode = this.getNextQuestion(this._activeQuestionSet$.value.answers) || '';
            this.setFocusByQuestionId(questionCode);
            this.focus.inputsOnDom$.next(0);
            this.focus.inputsOnDom$.complete();
          }
        } else {
          this.focus.inputsOnDom$.complete();
        }
      },
      (err: string) => {
        this.loggerService.postLogData(LogLevel.Error, 'application-form-service: subscribeControlToFocus', err, '');
      }
    );
  }

  private getNextQuestion(answers: IAnswer[]): string | undefined {
    const a = answers.find((ans) => ans.question.field_type !== 'label' && ans.question.field_type !== 'anchor');
    return a.question.id;
  }

  public setFocusByQuestionId(questionId: string | undefined): void {
    if (questionId) {
      const first = this._objBpApplication?.applicationQuestionSet$.value.answers.filter((ans) => ans.question.id === questionId);
      const el: HTMLElement | null =
        first && first.length > 0 ? document.getElementById(first[0].question.id) : document.getElementById(questionId);
      if (el) {
        if (el.nodeName == 'FIELDSET') {
          (el.firstElementChild as HTMLElement).focus();
        } else {
          el.style.visibility = 'visible';
          el.focus();
        }
      }
    }
  }

  public updateAnswer(answer: IAnswer): void {
    if (this._isAppSyncedWithBP$.value) {
      const queSet = this._appForm.questionSets.list.find((queSet) => queSet.question_set_id === answer.question.question_set_id);
      if (queSet) this._objBpApplication.updateActiveQuestionSet(queSet.question_set.code);

      this._objBpApplication.saveAnswer(answer);
    } else {
      const activeAnswer = this._appForm.questionSets.list
        .find((set) => set.question_set_id == answer.question.question_set_id)
        .answers.find((ans) => ans.question.id == answer.question.id);
      if (activeAnswer) {
        activeAnswer.value = answer.value;
        activeAnswer.choice_id = answer.choice_id;
        activeAnswer.choice_ids = answer.choice_ids;
        activeAnswer.is_completed = answer.is_completed;
      }
    }
  }

  public saveQuestionSet(): void {
    const app = this._appForm;
    const activeQueSet = app.questionSets.list.find((set) => set.is_completed === false);
    this._activeQuestionSet$.next(activeQueSet);
    if (activeQueSet.order >= 4) this.isAppFormRedirected$.next(true);
    else this.isAppFormRedirected$.next(false);

    const event = {
      type: ApplicationFormService.QUESET_SAVE_EVENT,
      data: app
    };
    console.log(ApplicationFormService.LOG_NAME, event);
    this.subscribeControlToFocus();
  }

  public updateBusinessInfoState(): void {
    if (!this._isAppSyncedWithBP$.value) {
      const app = this._appForm;
      let is_completed = false;
      for (let index = 0; index < 3; index++) {
        const questionSet = app.questionSets.list[index];
        is_completed = this.areAnswersComplete(questionSet);
        questionSet.is_completed = is_completed;
        if (!is_completed) break;
      }
      this._activeQuestionComplete$.next(is_completed);

      is_completed = false;
      for (let index = 0; index < app.questionSets.list.length; index++) {
        const questionSet = app.questionSets.list[index];
        if (!questionSet.is_completed) {
          is_completed = false;
          break;
        } else is_completed = true;
      }
      app.form.state = is_completed ? 'completed' : 'in_progress';

      const event = {
        type: ApplicationFormService.BUSINESS_INFO_STATE_EVENT,
        data: app
      };

      console.log(ApplicationFormService.LOG_NAME, event);
    } else {
      const app = this._appForm;
      let isEnabled = true;

      for (let index = 0; index < 3; index++) {
        isEnabled = this.areAnswersComplete(app.questionSets.list[index]);
        if (!isEnabled) break;
      }
      this._activeQuestionComplete$.next(isEnabled);
    }
  }

  public saveBusinessInfoQSet(): void {
    this._isAppFormRedirected$.next(true);
    this._isBridgingToSDKQSet$.next(true);
    const businessInfoQSets = this.application$.value.questionSets.list.slice(0, 3);
    let isAllBusinessInfoQSetsAllCompleted = true;
    businessInfoQSets.forEach((qSet) => {
      if (!qSet.is_completed && qSet.answers?.length > 0) {
        isAllBusinessInfoQSetsAllCompleted = false;
        const objUnSub$ = new Subject<void>();
        this._objBpApplication
          .saveQuestionSet(qSet.question_set.code)
          .pipe(
            takeUntil(objUnSub$),
            finalize(() => {
              objUnSub$.next();
              objUnSub$.complete();
            })
          )
          .subscribe(
            (val) => {
              setTimeout(() => {
                const activeQueSet = this._appForm.questionSets.list.find((set, index) => index > 2 && set.is_completed === false);
                this._objBpApplication.updateActiveQuestionSet(activeQueSet.question_set.code);
              }, 100);
            },
            (err: string) => {
              this.loggerService.postLogData(LogLevel.Error, 'application-form-service: saveBusinessInfoQSet', err, '');
            }
          );
      }
    });
    //this would be for if the question set is_completed on the business info page
    if (isAllBusinessInfoQSetsAllCompleted) {
      const activeQueSet = this._appForm.questionSets.list.find((set, index) => index > 2 && set.is_completed === false);
      if (activeQueSet) this.changeActiveQuestionSet(activeQueSet.order);
      else {
        this.changeActiveQuestionSet(4);
      }
      this._isBridgingToSDKQSet$.next(false);
    }
  }

  public get riskCode(): string {
    let product,
      riskCode: string = null;
    const coverageQuestionSet: IQuestionSet = this._appForm?.questionSets?.list.find((qs) => qs.question_set.title === 'Coverage');
    const productAnswer: IAnswer = coverageQuestionSet?.answers.find(
      (answer) => answer.question.code === 'progressive_commercial_coverage_types'
    );
    if (productAnswer) {
      const selectedChoice: IChoice = productAnswer.question.choices.find((choice) => choice.id === productAnswer.choice_id);
      product = selectedChoice?.alias;
      switch (product?.toUpperCase()) {
        case 'BI':
          riskCode = '';
          break;
        case 'BOP':
          riskCode = 'BP';
          break;
        case 'CGL':
          riskCode = 'GL';
          break;
        case 'PL':
          riskCode = 'PN';
          break;
        default:
          riskCode = product;
      }
    }
    return riskCode;
  }

  public get quoteNumber(): string {
    return this._appForm?.form?.reference_id;
  }

  public get stateCode(): string {
    let state: string = null;
    const contactQuestionSet: IQuestionSet = this._appForm?.questionSets?.list.find((qs) => qs.question_set.title === 'Contact');
    const contactAnswer: IAnswer = contactQuestionSet?.answers.find((answer) => answer.question.code === 'progressive_commercial_state');
    if (contactAnswer) {
      const selectedChoice: IChoice = contactAnswer.question.choices.find((choice) => choice.id === contactAnswer.choice_id);
      state = selectedChoice?.alias;
    }
    return state;
  }

  private subscribeToApplication() {
    this.application$
      .pipe(
        map((val: IApplication) => {
          this._appForm = val;
        }),
        catchError((err: string) => {
          this.loggerService.postLogData(LogLevel.Error, 'application-form-service: subscribeToApplication', err, '');
          return of(err);
        })
      )
      .subscribe();
  }

  private createPrefillData(): any {
    const answer_values = [];
    for (let index = 0; index < this._appForm.questionSets.list.length - 1; index++) {
      const questSet = this._appForm.questionSets.list[index];
      for (let index = 0; index < questSet.answers.length; index++) {
        const ans = questSet.answers[index];
        if (ans.is_completed) {
          if (ans.question.field_type === 'dropdown') {
            if (ans.question.code === 'progressive_commercial_naics') {
              const value = {
                code: ans.question.code,
                answer: ans.value
              };
              answer_values.push(value);
            } else if (ans.question.code === 'progressive_commercial_product') {
              const value = {
                code: ans.question.code,
                answer: ans.question.multiple
                  ? this.choiceText(ans.question.choices.find((choice) => choice.id === ans.choice_ids[0]))
                  : this.choiceText(ans.question.choices.find((choice) => choice.id === ans.choice_id))
              };
              answer_values.push(value);
            } else {
              const value = {
                code: ans.question.code,
                answer: this.choiceText(ans.question.choices.find((choice) => choice.id === ans.choice_id))
              };
              answer_values.push(value);
            }
          } else {
            const value = {
              code: ans.question.code,
              answer: ans.value
            };
            answer_values.push(value);
          }
        }
      }
    }
    const prefillData = {
      application_form: {
        mark_in_progress: true,
        autocomplete: true,
        answer_values: answer_values
      }
    };
    return prefillData;
  }

  private choiceText(choice: IChoice) {
    if (choice.alias) {
      return choice.alias;
    } else {
      return choice.value;
    }
  }

  private setConsentAcknowledgmentFromQuestionSet(activeQueSet: IQuestionSet) {
    this._showConsentAcknowledgmentCheckbox$.next(
      activeQueSet.question_set?.title === this._CONSENT_TITLE && activeQueSet.question_set.code === this._CONSENT_CODE
    );
  }

  private initFormDataListener() {
    this._objBpApplication.applicationForm$
      .pipe(
        map((form) => {
          if (form) {
            this._appForm.form = form;
            this._application$.next(this._appForm);
            if (this._appForm.form.id && sessionStorage.getItem('applicationId') && !sessionStorage.getItem('quoteNumber')) {
              this.loggerService.postLogData(
                LogLevel.Info,
                'Synced with BP: applicationIds and quoteNumber',
                JSON.stringify({
                  originalApplicationId: sessionStorage.getItem('applicationId'),
                  applicationId: this._appForm.form.id,
                  quoteNumber: this._appForm.form.reference_id
                })
              );
              sessionStorage.removeItem('applicationId'); //  PREVENTS LOGGING ON A PAGE REFRESH
            }
            if (this._appForm.form.state === ApplicationFormState.QUOTED || this._appForm.form.state === ApplicationFormState.COMPLETED) {
              this._isAppSyncedWithBP$.next(true);
              this.isAppFormRedirected$.next(true);
              this._isBridgingToSDKQSet$.next(false);
            }
          }
        }),
        catchError((error) => {
          this.loggerService.postLogData(LogLevel.Error, 'application-form-service: initFormDataListener applicationForm$', error, '');
          return of(error);
        }),
        map((logResponse) => {
          if (logResponse instanceof Error) {
            this.logger.error(logResponse);
          }
        })
      )
      .subscribe();

    combineLatest([this._objBpApplication.applicationQuestionSets$, this._objBpApplication.applicationParentQuestionSets$])
      .pipe(takeUntil(this._objQueSetsUnSub$))
      .subscribe(
        ([queSets, parentQueSets]) => {
          if (this._appForm && queSets?.length > 0) {
            this._appForm.questionSets.list = queSets.map((qs) => this.mapParentTitle(qs));
            this._application$.next(this._appForm);
            const areAllQuestionSetsCompleted = this.areAllQuestionSetsCompleted(queSets, parentQueSets);

            this._isReferred =
              this._appForm.questionSets.list?.length >= 4 &&
              areAllQuestionSetsCompleted &&
              this._appForm.form.state === ApplicationFormState.IN_PROGRESS &&
              this._appForm.questionSets?.list[this._appForm.questionSets.list?.length - 1].question_set.code !==
                ApplicationQuestionSetCodes.CONSENT;

            if (
              this._appForm.form.state === ApplicationFormState.QUOTED ||
              this._appForm.form.state === ApplicationFormState.COMPLETED ||
              this.isReferred
            ) {
              this._isAppSyncedWithBP$.next(true);
              this.isAppFormRedirected$.next(true);
            }
          }
        },
        (err: string) => {
          this.loggerService.postLogData(
            LogLevel.Error,
            'application-form-service: initFormDataListener applicationQuestionSets$',
            err,
            ''
          );
          this._objQueSetsUnSub$.next();
          this._objQueSetsUnSub$.complete();
        }
      );
    this._objBpApplication.applicationQuestionSet$.pipe(takeUntil(this._objQueSetUnSub$)).subscribe(
      (queSet) => {
        if (this._appForm && queSet) {
          this._appForm.questionSets.active = queSet.id;
          this._activeQuestionSet$.next(this.mapParentTitle(queSet));
          this._application$.next(this._appForm);
          this._isAppSyncedWithBP$.next(true);
          if (queSet.order >= 4) {
            this._sdkActiveQueSetTriggered = true;
            this._isBridgingToSDKQSet$.next(false);
            this.isAppFormRedirected$.next(true);
          } else {
            this.isAppFormRedirected$.next(false);
            this.updateBusinessInfoState();
          }
        }
      },
      (err: string) => {
        this.loggerService.postLogData(LogLevel.Error, 'application-form-service: initFormDataListener applicationQuestionSet$', err, '');
        this._objQueSetUnSub$.next();
        this._objQueSetUnSub$.complete();
      }
    );

    combineLatest([this._objBpApplication.applicationNonEmptyQuestionSets$, this._objBpApplication.applicationParentQuestionSets$])
      .pipe(takeUntil(this._objNEQueSetsUnSub$))
      .subscribe(
        ([qSets]) => {
          this._nonEmptyQuestionSets$.next(qSets.filter((qs) => !qs.is_blank).map((qs) => this.mapParentTitle(qs)));
        },
        (err: string) => {
          this.loggerService.postLogData(LogLevel.Error, 'application-form-service: initFormDataListener nonEmptyQuestionSets$', err, '');
          this._objNEQueSetsUnSub$.next();
          this._objNEQueSetsUnSub$.complete();
        }
      );

    this._objBpApplication.applicationQuestionSetId$.pipe(takeUntil(this._objQueSetsUnSub$)).subscribe(
      (questionSetId) => {
        if (questionSetId) {
          this._activeQuestionSetId$.next(questionSetId);
          const qSet = this._appForm?.questionSets?.list?.find((qSet) => qSet.question_set_id === questionSetId);
          if (qSet) this.setConsentAcknowledgmentFromQuestionSet(qSet);
          if (this._appForm?.form.state === 'in_progress' && !this._sdkActiveQueSetTriggered) {
            this.setAdHocActiveQuestionSet(questionSetId);
          }
        }
      },
      (err: string) => {
        this.loggerService.postLogData(LogLevel.Error, 'application-form-service: initFormDataListener applicationQuestionSetId$', err, '');
        this._objQueSetUnSub$.next();
        this._objQueSetUnSub$.complete();
      }
    );

    this._objBpApplication.applicationState$.pipe(takeUntil(this._objSdkStateUnSub$)).subscribe(
      (val) => {
        this._appFormState$.next(val);
      },
      (err: string) => {
        this.loggerService.postLogData(LogLevel.Error, 'application-form-service: initFormDataListener applicationState$', err, '');
        this._objSdkStateUnSub$.next();
        this._objSdkStateUnSub$.complete();
      }
    );

    this._objBpApplication.isApplicationFormLoading$.pipe(takeUntil(this._objSDKAppFormLoadingUnSub$)).subscribe(
      (val) => {
        this._isSDKFormLoading$.next(val);
      },
      (err: string) => {
        this.loggerService.postLogData(LogLevel.Error, 'application-form-service: initFormDataListener isApplicationFormLoading$', err, '');
        this._objSDKAppFormLoadingUnSub$.next();
        this._objSDKAppFormLoadingUnSub$.complete();
      }
    );
    this._objBpApplication.isQuestionSetLoading$.pipe(takeUntil(this._objQueSetLoadingUnSub$)).subscribe(
      (val) => {
        this._isActiveQuestionSetLoading$.next(val);
      },
      (err: string) => {
        this.loggerService.postLogData(LogLevel.Error, 'application-form-service: initFormDataListener isQuestionSetLoading$', err, '');
        this._objQueSetLoadingUnSub$.next();
        this._objQueSetLoadingUnSub$.complete();
      }
    );

    this._objBpApplication.isApplicationQuotesLoading$.pipe(takeUntil(this._objQuotesLoadingUnSub$)).subscribe(
      (val) => {
        this._areQuotesLoading$.next(val);
      },
      (err: string) => {
        this.loggerService.postLogData(
          LogLevel.Error,
          'application-form-service: initFormDataListener isApplicationQuotesLoading$',
          err,
          ''
        );
        this._objQuotesLoadingUnSub$.next();
        this._objQuotesLoadingUnSub$.complete();
      }
    );

    this._objBpApplication.applicationQuoteRequests$.pipe(takeUntil(this._objQuotesReqUnSub$)).subscribe(
      (val) => {
        this._applicationQuoteRequests$.next(val);
        if (val.length > 0) {
          this._isAppSyncedWithBP$.next(true);
        }
      },
      (err: string) => {
        this.loggerService.postLogData(LogLevel.Error, 'application-form-service: initFormDataListener applicationQuoteRequests$', err, '');
        this._objQuotesReqUnSub$.next();
        this._objQuotesReqUnSub$.complete();
      }
    );

    this._objBpApplication.isOwner$.pipe(takeUntil(this._objOwnerUnSub$)).subscribe(
      (val) => {
        this._isOwner$.next(val);
      },
      (err: string) => {
        this.loggerService.postLogData(LogLevel.Error, 'application-form-service: initFormDataListener isOwner$', err, '');
        this._objOwnerUnSub$.next();
        this._objOwnerUnSub$.complete();
      }
    );

    this._objBpApplication.isLocked$.pipe(takeUntil(this._objLockedUnSub$)).subscribe(
      (val) => {
        this._isLocked$.next(val);
      },
      (err: string) => {
        this.loggerService.postLogData(LogLevel.Error, 'application-form-service: initFormDataListener isLocked$', err, '');
        this._objLockedUnSub$.next();
        this._objLockedUnSub$.complete();
      }
    );
    this._objBpApplication.isTakenOver$.pipe(takeUntil(this._objTakenOverUnSub$)).subscribe(
      (val) => {
        this._isTakenOver$.next(val);
      },
      (err: string) => {
        this.loggerService.postLogData(LogLevel.Error, 'application-form-service: initFormDataListener isTakenOver$', err, '');
        this._objTakenOverUnSub$.next();
        this._objTakenOverUnSub$.complete();
      }
    );

    this._objBpApplication.isFormCloned$.pipe(takeUntil(this._objClonedUnSub$)).subscribe(
      (val) => {
        this._isFormCloned$.next(val);
      },
      (err: string) => {
        this.loggerService.postLogData(LogLevel.Error, 'application-form-service: initFormDataListener isFormCloned$', err, '');
        this._objClonedUnSub$.next();
        this._objClonedUnSub$.complete();
      }
    );

    this._objBpApplication.isFormInactive$.pipe(takeUntil(this._objInactiveUnSub$)).subscribe(
      (val) => {
        this._isFormInactive$.next(val);
      },
      (err: string) => {
        this.loggerService.postLogData(LogLevel.Error, 'application-form-service: initFormDataListener isFormInactive$', err, '');
        this._objInactiveUnSub$.next();
        this._objInactiveUnSub$.complete();
      }
    );

    this._objBpApplication.isFormLoaded$.pipe(takeUntil(this._objFormLoadedUnSub$)).subscribe(
      (val) => {
        this._isFormLoaded$.next(val);
      },
      (err: string) => {
        this.loggerService.postLogData(LogLevel.Error, 'application-form-service: initFormDataListener isFormLoaded$', err, '');
        this._objFormLoadedUnSub$.next();
        this._objFormLoadedUnSub$.complete();
      }
    );

    this._objBpApplication.selectLegacyMessages$.pipe(takeUntil(this._objGetMessagesUnSub$)).subscribe(
      (val) => {
        this._selectLegacyMessages$.next(val);
      },
      (err: string) => {
        this.loggerService.postLogData(LogLevel.Error, 'application-form-service: initFormDataListener selectLegacyMessages$', err, '');
        this._objGetMessagesUnSub$.next();
        this._objGetMessagesUnSub$.complete();
      }
    );
    this._objBpApplication.carrierProducts$.pipe(takeUntil(this._objCarrierProductsUnSub$)).subscribe(
      (carrierProducts) => {
        this._carrierProducts$.next(carrierProducts);
      },
      (err: string) => {
        this.loggerService.postLogData(LogLevel.Error, 'application-form-service: initFormDataListener carrierProducts$', err, '');
        this._objCarrierProductsUnSub$.next();
        this._objCarrierProductsUnSub$.complete();
      }
    );
  }

  private areAllQuestionSetsCompleted(queSets: IApplicationFormQuestionSet[], parentQueSets: IApplicationFormQuestionSet[]): boolean {
    if (!queSets?.length) return false;
    const parentCompleted = new Map(parentQueSets?.map((p) => [p.id, p.is_completed]) ?? []);
    return queSets.every(
      ({ is_completed, parentQuestionSetId }) =>
        is_completed && (!parentQuestionSetId || (parentCompleted.get(parentQuestionSetId) ?? false))
    );
  }

  private areAnswersComplete(questionSet: IQuestionSet): boolean {
    const incompleteAnswer = questionSet.answers.filter((answer) => answer.required && !answer.is_completed);
    if (incompleteAnswer.length > 0) {
      return false;
    }
    return true;
  }
  private setAdHocActiveQuestionSet(activeQuestionSetId): void {
    const adHocActiveQuestionSet = this._appForm?.questionSets?.list.find((qSet) => qSet.question_set_id === activeQuestionSetId);
    if (adHocActiveQuestionSet) {
      this._objBpApplication.updateActiveQuestionSet(adHocActiveQuestionSet.question_set.code);
      this._appForm.questionSets.active = adHocActiveQuestionSet.id;
      this._activeQuestionSet$.next(adHocActiveQuestionSet);
      this.setConsentAcknowledgmentFromQuestionSet(this.activeQuestionSet$.value);
      this._application$.next(this._appForm);
      this._isAppSyncedWithBP$.next(true);
      if (adHocActiveQuestionSet.order >= 4) {
        this._isBridgingToSDKQSet$.next(false);
        this.isAppFormRedirected$.next(true);
        this._isReferred = false;
      }
    }
  }
  private mapParentTitle(qs: IApplicationFormQuestionSet): IQuestionSet {
    if (!qs?.parentQuestionSetId) return qs;
    const parent = this._objBpApplication.applicationParentQuestionSets$.value.find(
      (p) => p.id === qs.parentQuestionSetId
    ) as IApplicationFormQuestionSet;
    if (!parent?.question_set?.title) return qs;
    let qsTitle = qs.question_set.title;
    if (parent.question_set.title === this._MQS_PARENT_TITLE) {
      if (!this._MQS_NOPREFIX_CHILD_TITLES.has(qsTitle)) {
        qsTitle = `${parent.question_set.title}-${qsTitle}`;
      }
    } else {
      qsTitle = parent.question_set.title;
    }
    return { ...qs, question_set: { ...qs.question_set, title: qsTitle } };
  }
  private destroyOpenSubscription(): void {
    this._objFormUnSub$.next();
    this._objFormUnSub$.complete();
    this._objQueSetUnSub$.next();
    this._objQueSetUnSub$.complete();
    this._objQueSetsUnSub$.next();
    this._objQueSetsUnSub$.complete();
    this._objNEQueSetsUnSub$.next();
    this._objNEQueSetsUnSub$.complete();
    this._objSdkStateUnSub$.next();
    this._objSdkStateUnSub$.complete();
    this._objQueSetLoadingUnSub$.next();
    this._objQueSetLoadingUnSub$.complete();
    this._objSDKAppFormLoadingUnSub$.next();
    this._objSDKAppFormLoadingUnSub$.complete();
    this._objQuotesLoadingUnSub$.next();
    this._objQuotesLoadingUnSub$.complete();
    this._objQuotesReqUnSub$.next();
    this._objQuotesReqUnSub$.complete();
    this._objOwnerUnSub$.next();
    this._objOwnerUnSub$.complete();
    this._objLockedUnSub$.next();
    this._objLockedUnSub$.complete();
    this._objTakenOverUnSub$.next();
    this._objTakenOverUnSub$.complete();
    this._objGetMessagesUnSub$.next();
    this._objGetMessagesUnSub$.complete();
    this._objCarrierProductsUnSub$.next();
    this._objCarrierProductsUnSub$.complete();
  }
}
